import React, { useState, useEffect, useCallback } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { SubscriptionCard } from '@/components/SubscriptionCard';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Building2, MapPin, Phone, Save, Smartphone, ExternalLink, Copy, Download, AlertCircle, Key, Loader2, Clock, RefreshCw, ShieldCheck } from 'lucide-react';
import { validateCNPJ, generateSlug } from '../utils/validation';
import { useCnpjLookup } from '../hooks/useCnpjLookup';

const Empresa = ({ user, onLogout }) => {
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [cnpjError, setCnpjError] = useState('');
  const { buscarCNPJ, loadingCNPJ } = useCnpjLookup();
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordData, setResetPasswordData] = useState({ email: '', newPassword: '', confirmPassword: '' });
  const [resetLoading, setResetLoading] = useState(false);
  const [recoveryRequests, setRecoveryRequests] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    segment: '',
    slug: '',
    codigo_acesso: null,
    razao_social: '',
    nome_fantasia: '',
    cnpj: '',
    inscricao_estadual: '',
    inscricao_municipal: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    cep: '',
    telefone_fixo: '',
    celular_whatsapp: '',
    email_empresa: '',
    site: '',
    contato_principal: '',
    app_url: 'https://verticeobra.com',
  });

  const [company, setCompany] = useState(JSON.parse(localStorage.getItem('company') || '{}'));
  const [gerandoCodigo, setGerandoCodigo] = useState(false);

  useEffect(() => {
    fetchCompanyData();
  }, []);

  // Auto-gerar slug quando o nome da empresa mudar e slug estiver vazio
  useEffect(() => {
    if (formData.name && !formData.slug) {
      const autoSlug = generateSlug(formData.name);
      setFormData(prev => ({ ...prev, slug: autoSlug }));
    }
  }, [formData.name]);

  // Buscar solicitações de recuperação de senha
  const fetchRecoveryRequests = useCallback(async () => {
    if (!company?.id) return;
    try {
      const response = await axiosInstance.get(`/auth/recovery-requests/${company.id}`);
      setRecoveryRequests(response.data.requests || []);
    } catch (error) {
      console.error('Erro ao buscar solicitações:', error);
    }
  }, [company?.id]);

  // Buscar solicitações periodicamente
  useEffect(() => {
    fetchRecoveryRequests();
    const interval = setInterval(fetchRecoveryRequests, 30000); // A cada 30 segundos
    return () => clearInterval(interval);
  }, [fetchRecoveryRequests]);

  // Validar CNPJ quando mudar
  const handleCNPJChange = async (value) => {
    const formatted = formatCNPJ(value);
    setFormData(prev => ({ ...prev, cnpj: formatted }));

    if (formatted.length === 18) {
      if (!validateCNPJ(formatted)) {
        setCnpjError('CNPJ inválido');
      } else {
        setCnpjError('');
        const digits = formatted.replace(/\D/g, '');
        const dados = await buscarCNPJ(digits);
        if (dados) {
          setFormData(prev => ({
            ...prev,
            razao_social: dados.razao_social || prev.razao_social,
            nome_fantasia: dados.nome_fantasia || prev.nome_fantasia,
            name: dados.nome_fantasia || dados.razao_social || prev.name,
            cep: dados.cep || prev.cep,
            logradouro: dados.logradouro || prev.logradouro,
            numero: dados.numero || prev.numero,
            complemento: dados.complemento || prev.complemento,
            bairro: dados.bairro || prev.bairro,
            cidade: dados.municipio || prev.cidade,
            estado: dados.uf || prev.estado,
            telefone_fixo: dados.telefone || prev.telefone_fixo,
            email_empresa: dados.email || prev.email_empresa,
          }));
        }
      }
    } else {
      setCnpjError('');
    }
  };

  const fetchCompanyData = async () => {
    setLoadingData(true);
    
    try {
      // Se não há empresa no localStorage, buscar do backend
      if (!company.id) {
        const companiesResponse = await axiosInstance.get(`/companies/${user.id}`);
        if (companiesResponse.data && companiesResponse.data.length > 0) {
          const companyData = companiesResponse.data[0];
          localStorage.setItem('company', JSON.stringify(companyData));
          setCompany(companyData);
          
          // Preencher formulário com dados da empresa
          setFormData({
            name: companyData.name || '',
            segment: companyData.segment || '',
            slug: companyData.slug || '',
            codigo_acesso: companyData.codigo_acesso || null,
            razao_social: companyData.razao_social || '',
            nome_fantasia: companyData.nome_fantasia || '',
            cnpj: companyData.cnpj || '',
            inscricao_estadual: companyData.inscricao_estadual || '',
            inscricao_municipal: companyData.inscricao_municipal || '',
            logradouro: companyData.logradouro || '',
            numero: companyData.numero || '',
            complemento: companyData.complemento || '',
            bairro: companyData.bairro || '',
            cidade: companyData.cidade || '',
            estado: companyData.estado || '',
            cep: companyData.cep || '',
            telefone_fixo: companyData.telefone_fixo || '',
            celular_whatsapp: companyData.celular_whatsapp || '',
            email_empresa: companyData.email_empresa || '',
            site: companyData.site || '',
            contato_principal: companyData.contato_principal || '',
            app_url: companyData.app_url || 'https://verticeobra.com',
          });
        } else {
          // Nenhuma empresa encontrada - deixar formulário vazio para criar nova
          setLoadingData(false);
          return;
        }
      } else {
        // Já tem empresa no localStorage, buscar dados atualizados
        const response = await axiosInstance.get(`/company/${company.id}`);
        setFormData({
          name: response.data.name || '',
          segment: response.data.segment || '',
          slug: response.data.slug || '',
          codigo_acesso: response.data.codigo_acesso || null,
          razao_social: response.data.razao_social || '',
          nome_fantasia: response.data.nome_fantasia || '',
          cnpj: response.data.cnpj || '',
          inscricao_estadual: response.data.inscricao_estadual || '',
          inscricao_municipal: response.data.inscricao_municipal || '',
          logradouro: response.data.logradouro || '',
          numero: response.data.numero || '',
          complemento: response.data.complemento || '',
          bairro: response.data.bairro || '',
          cidade: response.data.cidade || '',
          estado: response.data.estado || '',
          cep: response.data.cep || '',
          telefone_fixo: response.data.telefone_fixo || '',
          celular_whatsapp: response.data.celular_whatsapp || '',
          email_empresa: response.data.email_empresa || '',
          site: response.data.site || '',
          contato_principal: response.data.contato_principal || '',
          app_url: response.data.app_url || 'https://verticeobra.com',
        });
      }
    } catch (error) {
      console.error('Erro ao carregar empresa:', error);
      toast.error('Erro ao carregar dados da empresa');
    } finally {
      setLoadingData(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar CNPJ se preenchido
    if (formData.cnpj && formData.cnpj.length === 18) {
      if (!validateCNPJ(formData.cnpj)) {
        toast.error('CNPJ inválido. Por favor, verifique o número informado.');
        return;
      }
    }
    
    // Auto-gerar slug se não preenchido
    let dataToSend = { ...formData };
    if (!dataToSend.slug && dataToSend.name) {
      dataToSend.slug = generateSlug(dataToSend.name);
    }
    
    setLoading(true);

    try {
      const userId = user?.id || user?.user_id;
      
      // Se não existe empresa, criar uma nova
      if (!company.id) {
        const response = await axiosInstance.post('/companies', {
          user_id: userId,
          ...dataToSend,
        });
        
        // Atualizar localStorage com a nova empresa
        // O backend retorna {message, company_id, name}
        const newCompany = {
          id: response.data.company_id,
          name: response.data.name || dataToSend.name,
          segment: dataToSend.segment,
          slug: dataToSend.slug,
          user_id: userId,
          ...dataToSend
        };
        localStorage.setItem('company', JSON.stringify(newCompany));
        setCompany(newCompany);
        
        toast.success('Empresa cadastrada com sucesso!');
      } else {
        // Se existe empresa, atualizar
        await axiosInstance.put(`/company/${company.id}`, {
          user_id: userId,
          ...dataToSend,
        });
        
        // Atualizar localStorage
        const updatedCompany = { ...company, name: dataToSend.name, segment: dataToSend.segment, slug: dataToSend.slug };
        localStorage.setItem('company', JSON.stringify(updatedCompany));
        
        toast.success('Dados da empresa atualizados com sucesso!');
      }
      
      // Atualizar formData com o slug gerado
      setFormData(dataToSend);
    } catch (error) {
      console.error('Erro ao salvar empresa:', error);
      const message = error.response?.data?.detail || 'Erro ao salvar dados da empresa';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (resetPasswordData.newPassword !== resetPasswordData.confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }
    
    if (resetPasswordData.newPassword.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    
    setResetLoading(true);
    
    try {
      await axiosInstance.post('/auth/reset-password', {
        admin_user_id: user?.id || user?.user_id,
        target_email: resetPasswordData.email,
        new_password: resetPasswordData.newPassword
      });
      
      toast.success('Senha redefinida com sucesso!');
      setShowResetPasswordModal(false);
      setResetPasswordData({ email: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao redefinir senha';
      toast.error(message);
    } finally {
      setResetLoading(false);
    }
  };

  const formatCNPJ = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1');
  };

  const formatCEP = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .replace(/(-\d{3})\d+?$/, '$1');
  };

  const formatPhone = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .replace(/(-\d{4})\d+?$/, '$1');
  };

  const handleGerarCodigoAcesso = async () => {
    if (!company.id) {
      toast.error('Salve a empresa primeiro para gerar o código de acesso');
      return;
    }
    
    setGerandoCodigo(true);
    try {
      const response = await axiosInstance.post(`/company/${company.id}/gerar-codigo-acesso`);
      setFormData(prev => ({ ...prev, codigo_acesso: response.data.codigo_acesso }));
      toast.success(`Código de acesso gerado: ${response.data.codigo_acesso}`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao gerar código de acesso';
      toast.error(message);
    } finally {
      setGerandoCodigo(false);
    }
  };

  if (loadingData) {
    return (
      <div className="flex min-h-screen bg-zinc-950 text-white">
        <Sidebar user={user} onLogout={onLogout} activePage="empresa" />
        <div className="flex-1 p-8 flex items-center justify-center">
          <p className="text-zinc-400">Carregando dados da empresa...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-zinc-950 text-white">
      <Sidebar user={user} onLogout={onLogout} activePage="empresa" />

      <div className="flex-1 p-8">
        <div className="max-w-6xl mx-auto space-y-6">
          <SubscriptionCard user={user} />

          {/* Header */}
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold">Dados da Empresa</h1>
              <p className="text-zinc-400 mt-1">Gerencie as informações da sua empresa</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Card 1: Dados da Empresa */}
            <Card className="bg-zinc-900 border-zinc-800 border-l-4 border-l-blue-500">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Building2 className="w-5 h-5 mr-2" />
                  Dados da Empresa
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Nome da Empresa *</Label>
                  <Input
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>

                <div>
                  <Label>Segmento *</Label>
                  <Select
                    value={formData.segment}
                    onValueChange={(value) => setFormData({ ...formData, segment: value })}
                    required
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="comercio">Comércio</SelectItem>
                      <SelectItem value="servicos">Serviços</SelectItem>
                      <SelectItem value="industria">Indústria</SelectItem>
                      <SelectItem value="tecnologia">Tecnologia</SelectItem>
                      <SelectItem value="consultoria">Consultoria</SelectItem>
                      <SelectItem value="construcao">Construção</SelectItem>
                      <SelectItem value="saude">Saúde</SelectItem>
                      <SelectItem value="educacao">Educação</SelectItem>
                      <SelectItem value="outro">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Razão Social</Label>
                  <Input
                    value={formData.razao_social}
                    onChange={(e) => setFormData({ ...formData, razao_social: e.target.value })}
                    placeholder="Empresa LTDA"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>

                <div>
                  <Label>Nome Fantasia</Label>
                  <Input
                    value={formData.nome_fantasia}
                    onChange={(e) => setFormData({ ...formData, nome_fantasia: e.target.value })}
                    placeholder="Nome comercial"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>

                <div>
                  <Label className="flex items-center gap-2">
                    CNPJ
                    {loadingCNPJ && <Loader2 className="w-3 h-3 animate-spin text-zinc-400" />}
                  </Label>
                  <Input
                    value={formData.cnpj}
                    onChange={(e) => handleCNPJChange(e.target.value)}
                    placeholder="00.000.000/0000-00"
                    maxLength={18}
                    className={`bg-zinc-800 border-zinc-700 ${cnpjError ? 'border-red-500' : ''}`}
                  />
                  {cnpjError && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {cnpjError}
                    </p>
                  )}
                </div>

                <div>
                  <Label>Inscrição Estadual</Label>
                  <Input
                    value={formData.inscricao_estadual}
                    onChange={(e) => setFormData({ ...formData, inscricao_estadual: e.target.value })}
                    placeholder="000.000.000.000"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>

                <div>
                  <Label>Inscrição Municipal</Label>
                  <Input
                    value={formData.inscricao_municipal}
                    onChange={(e) => setFormData({ ...formData, inscricao_municipal: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Card 2: Endereço */}
            <Card className="bg-zinc-900 border-zinc-800 border-l-4 border-l-green-500">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  Endereço
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <Label>Logradouro</Label>
                  <Input
                    value={formData.logradouro}
                    onChange={(e) => setFormData({ ...formData, logradouro: e.target.value })}
                    placeholder="Rua, Avenida, etc."
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>

                <div>
                  <Label>Número</Label>
                  <Input
                    value={formData.numero}
                    onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                    placeholder="123"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>

                <div>
                  <Label>Complemento</Label>
                  <Input
                    value={formData.complemento}
                    onChange={(e) => setFormData({ ...formData, complemento: e.target.value })}
                    placeholder="Sala, Apto, etc."
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>

                <div>
                  <Label>Bairro</Label>
                  <Input
                    value={formData.bairro}
                    onChange={(e) => setFormData({ ...formData, bairro: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>

                <div>
                  <Label>Cidade</Label>
                  <Input
                    value={formData.cidade}
                    onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>

                <div>
                  <Label>Estado (UF)</Label>
                  <Input
                    value={formData.estado}
                    onChange={(e) => setFormData({ ...formData, estado: e.target.value.toUpperCase() })}
                    placeholder="SP"
                    maxLength={2}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>

                <div>
                  <Label>CEP</Label>
                  <Input
                    value={formData.cep}
                    onChange={(e) => setFormData({ ...formData, cep: formatCEP(e.target.value) })}
                    placeholder="00000-000"
                    maxLength={9}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Card 3: Contatos */}
            <Card className="bg-zinc-900 border-zinc-800 border-l-4 border-l-purple-500">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Phone className="w-5 h-5 mr-2" />
                  Contatos
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Telefone Fixo</Label>
                  <Input
                    value={formData.telefone_fixo}
                    onChange={(e) => setFormData({ ...formData, telefone_fixo: formatPhone(e.target.value) })}
                    placeholder="(00) 0000-0000"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>

                <div>
                  <Label>Celular / WhatsApp</Label>
                  <Input
                    value={formData.celular_whatsapp}
                    onChange={(e) => setFormData({ ...formData, celular_whatsapp: formatPhone(e.target.value) })}
                    placeholder="(00) 00000-0000"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>

                <div>
                  <Label>E-mail da Empresa</Label>
                  <Input
                    type="email"
                    value={formData.email_empresa}
                    onChange={(e) => setFormData({ ...formData, email_empresa: e.target.value })}
                    placeholder="contato@empresa.com"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>

                <div>
                  <Label>Site</Label>
                  <Input
                    value={formData.site}
                    onChange={(e) => setFormData({ ...formData, site: e.target.value })}
                    placeholder="www.empresa.com"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label>Nome do Contato Principal</Label>
                  <Input
                    value={formData.contato_principal}
                    onChange={(e) => setFormData({ ...formData, contato_principal: e.target.value })}
                    placeholder="Nome do responsável"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Card 4: Links dos Apps (Multi-tenant) */}
            <Card className="bg-zinc-900 border-zinc-800 border-l-4 border-l-purple-500">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <ExternalLink className="w-5 h-5 mr-2" />
                  Links dos Apps (Vendedor / Supervisor)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>URL Base do Sistema</Label>
                    <Input
                      value={formData.app_url}
                      onChange={(e) => setFormData({ ...formData, app_url: e.target.value })}
                      placeholder="https://lucroliquido.com"
                      className="bg-zinc-800 border-zinc-700"
                    />
                    <p className="text-xs text-zinc-500 mt-1">
                      Domínio onde o sistema está hospedado
                    </p>
                  </div>
                  <div>
                    <Label>Slug da Empresa (URL única)</Label>
                    <Input
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                      placeholder="minha-empresa"
                      className="bg-zinc-800 border-zinc-700 font-mono"
                    />
                    <p className="text-xs text-zinc-500 mt-1">
                      ✨ Auto-gerado a partir do nome da empresa. Você pode personalizar se preferir.
                    </p>
                  </div>
                </div>

                {/* Código de Acesso Numérico para Funcionários */}
                <div className="p-4 bg-gradient-to-r from-purple-950/50 to-blue-950/50 rounded-lg border border-purple-800/50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Key className="w-5 h-5 text-purple-400" />
                        <Label className="text-purple-300 font-semibold">Código de Acesso para Funcionários</Label>
                      </div>
                      <p className="text-xs text-zinc-400 mb-3">
                        Código numérico único para login de Engenheiros e Supervisores no sistema.
                      </p>
                      
                      {formData.codigo_acesso ? (
                        <div className="flex items-center gap-4">
                          <div className="bg-zinc-900 px-6 py-3 rounded-lg border border-purple-600">
                            <span className="text-3xl font-bold text-purple-400 font-mono tracking-widest">
                              {formData.codigo_acesso}
                            </span>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="border-purple-500 text-purple-400 hover:bg-purple-500/10"
                              onClick={() => {
                                const url = `${formData.app_url || window.location.origin}/api/acesso/${formData.codigo_acesso}`;
                                navigator.clipboard.writeText(url);
                                toast.success('Link de acesso copiado!');
                              }}
                            >
                              <Copy className="w-4 h-4 mr-2" /> Copiar Link
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                              onClick={() => {
                                const url = `${formData.app_url || window.location.origin}/api/acesso/${formData.codigo_acesso}`;
                                window.open(url, '_blank');
                              }}
                            >
                              <ExternalLink className="w-4 h-4 mr-2" /> Abrir Página
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          <div className="bg-zinc-800 px-4 py-2 rounded-lg border border-dashed border-zinc-600">
                            <span className="text-zinc-500 italic">Nenhum código gerado</span>
                          </div>
                          <Button
                            type="button"
                            onClick={handleGerarCodigoAcesso}
                            disabled={gerandoCodigo || !company.id}
                            className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                          >
                            {gerandoCodigo ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Gerando...
                              </>
                            ) : (
                              <>
                                <Key className="w-4 h-4 mr-2" /> Gerar Código
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {formData.codigo_acesso && formData.app_url && (
                    <div className="mt-4 pt-4 border-t border-purple-800/30">
                      <p className="text-xs text-zinc-400">
                        <strong className="text-purple-300">URL de Login:</strong>{' '}
                        <code className="bg-zinc-900 px-2 py-1 rounded text-blue-400">
                          {formData.app_url.replace(/\/$/, '')}/api/acesso/{formData.codigo_acesso}
                        </code>
                      </p>
                      <p className="text-xs text-zinc-500 mt-2">
                        💡 Compartilhe este código ou link com seus funcionários (Engenheiros e Supervisores) para que possam fazer login no sistema.
                      </p>
                    </div>
                  )}
                </div>

              </CardContent>
            </Card>

            {/* Card 5: App do Proprietário */}
            <Card className="bg-zinc-900 border-zinc-800 border-l-4 border-l-indigo-500">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Smartphone className="w-5 h-5 mr-2" />
                  App do Proprietário
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-zinc-400 text-sm">
                  Acompanhe o dashboard e relatórios da empresa pelo app. Instale no seu celular para acesso rápido aos indicadores financeiros, DRE, DFC e fluxo de caixa.
                </p>
                
                <div className="flex flex-wrap gap-3">
                  <Button
                    type="button"
                    onClick={() => {
                      const baseUrl = (formData.app_url || window.location.origin).replace(/\/$/, '');
                      const slug = formData.slug;
                      if (!slug) {
                        toast.error('Configure o Slug da empresa primeiro');
                        return;
                      }
                      window.open(`${baseUrl}/api/app/${slug}/proprietario`, '_blank');
                    }}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Abrir App
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const baseUrl = (formData.app_url || window.location.origin).replace(/\/$/, '');
                      const slug = formData.slug;
                      if (!slug) {
                        toast.error('Configure o Slug da empresa primeiro');
                        return;
                      }
                      const appUrl = `${baseUrl}/api/app/${slug}/proprietario`;
                      navigator.clipboard.writeText(appUrl);
                      toast.success('Link copiado para a área de transferência!');
                    }}
                    className="border-zinc-700 hover:bg-zinc-800"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copiar Link
                  </Button>
                  
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      toast.info('Para instalar: Abra o app no navegador do celular e clique em "Adicionar à Tela Inicial"');
                    }}
                    className="border-zinc-700 hover:bg-zinc-800"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Como Instalar
                  </Button>
                </div>

                <div className="mt-4 p-4 bg-zinc-800/50 rounded-lg">
                  <p className="text-xs text-zinc-500">
                    <strong>Dica:</strong> No iPhone, abra o link no Safari e toque em &quot;Compartilhar&quot; → &quot;Adicionar à Tela de Início&quot;. 
                    No Android, abra no Chrome e toque nos 3 pontos → &quot;Instalar app&quot; ou &quot;Adicionar à Tela inicial&quot;.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Card 6: Administração de Usuários */}
            <Card className="bg-zinc-900 border-zinc-800 border-l-4 border-l-amber-500">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Key className="w-5 h-5 mr-2 text-amber-500" />
                  Administração de Senhas
                </CardTitle>
                <CardDescription className="text-zinc-400">
                  Gerencie solicitações de recuperação de senha
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Solicitações de PIN pendentes */}
                {recoveryRequests.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium text-amber-400 flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4" />
                        Solicitações de PIN Pendentes
                      </h4>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={fetchRecoveryRequests}
                        className="text-zinc-400 hover:text-white"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                    </div>
                    {recoveryRequests.map((req) => (
                      <div 
                        key={req.id} 
                        className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-white">{req.user_name}</p>
                            <p className="text-sm text-zinc-400">{req.user_email}</p>
                          </div>
                          <div className="text-right">
                            <Badge className="bg-amber-500 text-black font-mono text-lg px-3">
                              {req.pin}
                            </Badge>
                            <p className="text-xs text-zinc-500 mt-1 flex items-center gap-1 justify-end">
                              <Clock className="w-3 h-3" />
                              Expira em {req.expires_in_minutes} min
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                    <p className="text-xs text-zinc-500">
                      Envie o PIN para o usuário pelo WhatsApp. O código expira em 30 minutos.
                    </p>
                  </div>
                )}

                {recoveryRequests.length === 0 && (
                  <div className="text-center py-4 text-zinc-500">
                    <ShieldCheck className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Nenhuma solicitação de recuperação pendente</p>
                  </div>
                )}

                <div className="border-t border-zinc-800 pt-4">
                  <p className="text-zinc-400 text-sm mb-3">
                    Você também pode redefinir a senha diretamente:
                  </p>
                  <Button
                    type="button"
                    onClick={() => setShowResetPasswordModal(true)}
                    variant="outline"
                    className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                  >
                    <Key className="w-4 h-4 mr-2" />
                    Redefinir Senha Manualmente
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Botão Salvar */}
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={loading}
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 px-8"
              >
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Salvando...' : 'Salvar Alterações'}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Modal de Redefinição de Senha */}
      <Dialog open={showResetPasswordModal} onOpenChange={setShowResetPasswordModal}>
        <DialogContent className="sm:max-w-md bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-500" />
              Redefinir Senha de Usuário
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Email do Usuário</Label>
              <Input
                type="email"
                placeholder="usuario@email.com"
                value={resetPasswordData.email}
                onChange={(e) => setResetPasswordData({ ...resetPasswordData, email: e.target.value })}
                required
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Nova Senha</Label>
              <Input
                type="password"
                placeholder="Mínimo 6 caracteres"
                value={resetPasswordData.newPassword}
                onChange={(e) => setResetPasswordData({ ...resetPasswordData, newPassword: e.target.value })}
                required
                minLength={6}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Confirmar Nova Senha</Label>
              <Input
                type="password"
                placeholder="Repita a nova senha"
                value={resetPasswordData.confirmPassword}
                onChange={(e) => setResetPasswordData({ ...resetPasswordData, confirmPassword: e.target.value })}
                required
                minLength={6}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowResetPasswordModal(false);
                  setResetPasswordData({ email: '', newPassword: '', confirmPassword: '' });
                }}
                className="border-zinc-700"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={resetLoading}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {resetLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Redefinindo...
                  </>
                ) : (
                  <>
                    <Key className="w-4 h-4 mr-2" />
                    Redefinir Senha
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Empresa;